import { useRef, useState } from 'react';
import { FolderOpen } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { collectFromDrop, collectFromInput, planImport, runImport } from '../lib/bulkImport';

const BulkImport = ({ onDone }) => {
  const [dragOver, setDragOver] = useState(false);
  const [plan, setPlan] = useState(null); // {items, ignored}
  const [stat, setStat] = useState(null);
  const [running, setRunning] = useState(false);
  const control = useRef({ cancelled: false });

  const prepare = (found) => {
    const p = planImport(found);
    if (p.items.length === 0) {
      toast.error('No posters found. Drop the "posters" folder (the one with anime, cars, … inside).');
      return;
    }
    setPlan(p);
    setStat(null);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    try {
      prepare(await collectFromDrop(e.dataTransfer));
    } catch (err) {
      toast.error('Could not read that folder. Try the "Choose folder" button.');
    }
  };

  const start = async () => {
    control.current = { cancelled: false };
    setRunning(true);
    try {
      await runImport(plan.items, setStat, control.current);
      toast.success('Import finished');
      onDone && onDone();
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      setRunning(false);
    }
  };

  const counts = plan
    ? plan.items.reduce((acc, it) => {
        acc[it.info.category] = (acc[it.info.category] || 0) + 1;
        return acc;
      }, {})
    : {};
  const pct = stat && stat.total ? Math.round((stat.done / stat.total) * 100) : 0;

  return (
    <div className="mb-10 border border-border/40 rounded-sm bg-secondary p-5" data-testid="bulk-import">
      <h2 className="font-heading font-bold text-xl text-foreground mb-1">Bulk import posters</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Drag your whole <b>posters</b> folder here. Each sub-folder becomes a category. Every picture is converted to
        WebP and gets a “poster on the wall” photo automatically. Keep this page open until it finishes.
      </p>

      {!running && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-sm px-4 py-8 text-center transition-colors ${
            dragOver ? 'border-white bg-white/10' : 'border-border'
          }`}
          data-testid="bulk-drop-zone"
        >
          <FolderOpen size={32} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-foreground">Drop the posters folder here</p>
          <label className="inline-block mt-3 text-xs underline text-muted-foreground cursor-pointer">
            or choose folder
            <input
              type="file"
              className="hidden"
              webkitdirectory=""
              directory=""
              multiple
              data-testid="bulk-folder-input"
              onChange={(e) => {
                prepare(collectFromInput(e.target.files));
                e.target.value = '';
              }}
            />
          </label>
        </div>
      )}

      {plan && (
        <div className="mt-4 text-sm text-foreground" data-testid="bulk-plan">
          <p className="mb-2">
            Found <b>{plan.items.length}</b> posters:{' '}
            {Object.entries(counts)
              .map(([c, n]) => `${c} ${n}`)
              .join(' · ')}
          </p>
          {plan.ignored.length > 0 && (
            <p className="text-xs text-muted-foreground mb-2">
              {plan.ignored.length} file(s) in unknown folders were ignored.
            </p>
          )}
          {!running && !(stat && stat.finished) && (
            <button
              type="button"
              onClick={start}
              className="bg-white text-black hover:bg-white/90 px-4 py-2 rounded-sm text-sm font-semibold"
              data-testid="bulk-start"
            >
              Import {plan.items.length} posters
            </button>
          )}
          {running && (
            <button
              type="button"
              onClick={() => {
                control.current.cancelled = true;
              }}
              className="border border-border px-4 py-2 rounded-sm text-sm"
              data-testid="bulk-stop"
            >
              Stop after this poster
            </button>
          )}
        </div>
      )}

      {stat && (
        <div className="mt-4" data-testid="bulk-progress">
          <div className="h-2 bg-background rounded-full overflow-hidden">
            <div className="h-full bg-white transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {stat.done}/{stat.total} · added {stat.added} · skipped {stat.skipped} · failed {stat.failed}
            {stat.current ? ` · working on ${stat.current}` : ''}
            {stat.finished ? (stat.cancelled ? ' · stopped' : ' · done ✔') : ''}
          </p>
          {stat.log.length > 0 && (
            <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-muted-foreground space-y-1" data-lenis-prevent>
              {stat.log.slice(-60).map((l, i) => (
                <li key={i} className={l.startsWith('FAILED') ? 'text-red-400' : ''}>
                  {l}
                </li>
              ))}
            </ul>
          )}
          {stat.finished && stat.failed > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              Drop the folder again and press Import — already-added posters are skipped, only failed ones are retried.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkImport;
